/**
 * Report-card chat SSE client: the transport layer beneath the chat adapter.
 * POSTs the chat request to the chatbot proxy with credentials and an optional
 * Bearer token, then parses the streamed `text/event-stream` response into a
 * discriminated union of frames (`text` / `error` / `done`) the adapter can
 * consume turn-by-turn. Every failure path — non-OK HTTP, missing stream body,
 * malformed JSON, the `[DONE]` sentinel, or a stream that closes without one —
 * resolves to an explicit frame so the UI never hangs in `streaming` state or
 * renders a raw HTTP status. The reader lock is always released on exit.
 */
import { createAbortTimer, IAbortTimer, RequestTimeoutError, transportTimeoutMs } from '../../../lib/helpers/transport-timeout.helpers';
import { SseParser } from './sse-parser';

export interface IRcSseClientOptions {
  url: string;
  body: unknown;
  abortSignal: AbortSignal;
  /** Bearer token for the proxy (authenticated users). Optional. */
  authToken?: string;
}

export type SseFrame =
  | { type: 'text'; text: string }
  | { type: 'error'; error: string }
  | { type: 'done' };

// Streams proxy SSE frames as discriminated-union events for the adapter.
// A JSON-parse failure becomes an error event, not a silent empty bubble.
export async function* streamChat({
  url,
  body,
  abortSignal,
  authToken,
}: IRcSseClientOptions): AsyncGenerator<SseFrame, void, undefined> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  // An idle bound, not a total one: every chunk restarts it, so only a silent stream is cut off.
  const timer = createAbortTimer('report-card chat', transportTimeoutMs('default'), abortSignal);
  try {
    yield* readChatStream(url, headers, body, timer);
  } catch (error) {
    const settled = timer.settleError(error);
    if (!(settled instanceof RequestTimeoutError)) throw settled;
    yield { type: 'error', error: 'The chat service stopped responding. Please try again.' };
  } finally {
    timer.dispose();
  }
}

async function* readChatStream(
  url: string,
  headers: Record<string, string>,
  body: unknown,
  timer: IAbortTimer,
): AsyncGenerator<SseFrame, void, undefined> {
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal: timer.signal,
    // Reuse the SPA's cookie/auth context for cookie-based flows; a Bearer token
    // in Authorization works too — the proxy accepts either.
    credentials: 'include',
  });

  if (!response.ok) {
    // Try to extract the proxy's JSON `{ message }` envelope; fall back to a
    // generic message so the UI never renders a raw HTTP status alone.
    let message: string | undefined;
    try {
      const errBody = await response.json();
      message = errBody && typeof errBody.message === 'string' ? errBody.message : undefined;
    } catch (error) {
      console.warn('streamChat: error response was not JSON; using the generic message', error);
    }
    yield { type: 'error', error: message || `Chat request failed (${response.status}).` };
    return;
  }

  if (!response.body) {
    yield { type: 'error', error: 'Streaming is not supported in this browser.' };
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  const parser = new SseParser();

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      timer.touch();

      const text = decoder.decode(value, { stream: true });
      const events = parser.push(text);
      for (const evt of events) {
        if (evt.data === '[DONE]') {
          yield { type: 'done' };
          return;
        }
        try {
          const payload = JSON.parse(evt.data);
          if (typeof payload.text === 'string') {
            yield { type: 'text', text: payload.text };
          } else if (typeof payload.error === 'string') {
            yield { type: 'error', error: payload.error };
            return;
          }
        } catch (_) {
          yield { type: 'error', error: 'Received malformed chat data.' };
          return;
        }
      }
    }
    // Stream ended without an explicit [DONE] sentinel — treat as a clean
    // close so the UI flips out of `streaming` state.
    yield { type: 'done' };
  } finally {
    try {
      reader.releaseLock();
    } catch (error) {
      console.warn('streamChat: reader lock already released', error);
    }
  }
}
